import { type FormEvent, useEffect, useRef, useState } from "react";
import { blueRose, sendCropped, x, yourEmail } from "../assets";
import "./SignupModal.css";

const MAILCHIMP_ACTION =
  "https://hernameisriver.us18.list-manage.com/subscribe/post?u=c2f331b980eef112713dc4f46&id=99a8c459d2&f_id=00bcb2e6f0";
/** Mailchimp's honeypot: a bot filling this in gets silently dropped. */
const HONEYPOT_FIELD = "b_c2f331b980eef112713dc4f46_99a8c459d2";
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Mailing-list signup. Posts to Mailchimp no-cors (so the response is opaque
 * and any non-network failure reads as success), then plays the rose clip.
 */
export function SignupModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const formRef = useRef<HTMLFormElement>(null);
  const roseVideoRef = useRef<HTMLVideoElement>(null);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState<{ text: string; error: boolean } | null>(null);
  const [subscribed, setSubscribed] = useState(false);

  // The rose is a short loop; browsers sometimes stall it on the first frame.
  useEffect(() => {
    const video = roseVideoRef.current;
    if (!video) return;
    const resume = () => {
      if (subscribed && video.paused) {
        video.play().catch(() => setTimeout(resume, 150));
      }
    };
    video.addEventListener("pause", resume);
    return () => video.removeEventListener("pause", resume);
  }, [subscribed]);

  function close() {
    onClose();
    setSubscribed(false);
    setMessage(null);
    roseVideoRef.current?.pause();
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    if (!EMAIL_PATTERN.test(email.trim())) {
      setMessage({ text: "Please enter a valid email address.", error: true });
      return;
    }

    const params = new URLSearchParams();
    new FormData(e.currentTarget).forEach((value, key) => {
      params.append(key, String(value));
    });

    setMessage({ text: "Sending...", error: false });

    try {
      await fetch(MAILCHIMP_ACTION, { method: "POST", mode: "no-cors", body: params });
      setMessage(null);
      setSubscribed(true);
      setEmail("");
      const video = roseVideoRef.current;
      if (video) {
        video.currentTime = 0;
        video.play();
      }
    } catch {
      setMessage({ text: "Something went wrong. Please try again.", error: true });
    }
  }

  return (
    <div
      id="signup-overlay"
      data-audio="keep"
      className={open ? "open" : undefined}
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div id="signup-modal" style={{ backgroundImage: subscribed ? "none" : `url(${yourEmail})` }}>
        <button type="button" id="signup-close" aria-label="Stäng" onClick={close}>
          <img src={x} alt="Stäng" />
        </button>
        <form
          ref={formRef}
          action={MAILCHIMP_ACTION}
          method="post"
          id="mc-embedded-subscribe-form"
          name="mc-embedded-subscribe-form"
          noValidate
          onSubmit={onSubmit}
          style={subscribed ? { display: "none" } : undefined}
        >
          <input
            type="email"
            name="EMAIL"
            id="mce-EMAIL"
            placeholder=""
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <div style={{ position: "absolute", left: "-5000px" }} aria-hidden="true">
            <input type="text" name={HONEYPOT_FIELD} tabIndex={-1} defaultValue="" />
          </div>
          <button type="submit" id="mc-embedded-subscribe">
            <img src={sendCropped} alt="Skicka" />
          </button>
        </form>
        <div
          id="signup-msg"
          className={message ? `show${message.error ? " error" : ""}` : undefined}
        >
          {message?.text}
        </div>

        <div id="signup-rose" className={subscribed ? "show" : undefined}>
          <video ref={roseVideoRef} id="rose-video" muted playsInline loop>
            <source src={blueRose} type="video/webm" />
          </video>
        </div>
      </div>
    </div>
  );
}
